/**
 * "Registrar no prontuário" — grava uma mensagem do chat como evolução na
 * ficha do paciente.
 *
 * O paciente já vem escolhido: o vinculado à mensagem ("citar paciente") e,
 * sem vínculo, o da ficha aberta. O médico ainda revisa o texto e pode trocar
 * o paciente antes de gravar — o que entra no prontuário é dele.
 *
 * Entra como `CONSULTATION` com rótulo "Recado do chat" e um snapshot da
 * origem (`fromChat`): a mensagem pode ser apagada do chat, o registro não.
 * Como toda evolução, só dá pra editar/excluir nas primeiras 24h.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { PatientTypeahead } from '@/components/patients/PatientTypeahead';
import { ClinicalRecordService } from '@/services/firebase/records';
import { PatientService } from '@/services/firebase/patients';
import type { ChatMessageData } from '@/services/firebase/chat';
import {
  CHAT_RECORD_TITLE,
  buildChatRecordText,
  formatChatRecordWhen,
  roleCanRegisterChatInRecord,
} from '@/utils/chatToRecord';
import type { Patient } from '@/types/models';

/** Quem pode registrar é quem grava evolução: médico e admin. */
export function useCanRegisterChatInRecord(): boolean {
  const { role } = useAuth();
  return roleCanRegisterChatInRecord(role);
}

interface Props {
  clinicId: string;
  message: ChatMessageData | null;
  /** Ex.: "#recepção" ou "@Clinoft BS" — entra no texto do registro. */
  channelLabel?: string;
  /** Ficha aberta na tela — paciente sugerido quando a mensagem não tem vínculo. */
  fallbackPatientId?: string;
  onClose: () => void;
}

export const ChatToRecordModal = ({ clinicId, message, channelLabel, fallbackPatientId, onClose }: Props) => {
  const { user } = useAuth();
  const [patient, setPatient] = useState<Patient | null>(null);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  // Só abre depois de achar o paciente sugerido — se ele chegasse com o
  // pop-up aberto, apareceria "do nada" no campo enquanto o médico lê.
  const [ready, setReady] = useState(false);
  const suggestedId = message?.linkedPatientId || fallbackPatientId;
  useEffect(() => {
    setPatient(null);
    setError(null);
    setText(message ? buildChatRecordText(message, channelLabel) : '');
    if (!message) return;
    if (!suggestedId) {
      setReady(true);
      return;
    }
    setReady(false);
    let cancelled = false;
    PatientService.getPatient(clinicId, suggestedId)
      .then((p) => {
        if (!cancelled && p) setPatient(p as Patient);
      })
      .catch((err) => console.error('[chat→prontuário] paciente sugerido:', err))
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [clinicId, message, suggestedId, channelLabel]);

  useEffect(() => {
    if (!done) return;
    const t = setTimeout(() => setDone(null), 3000);
    return () => clearTimeout(t);
  }, [done]);

  const submit = async () => {
    const body = text.trim();
    if (!message || !patient || !body || saving || !user) return;
    setSaving(true);
    setError(null);
    try {
      await ClinicalRecordService.createRecord(clinicId, patient.id, {
        professionalId: user.uid,
        type: 'CONSULTATION',
        displayTitle: CHAT_RECORD_TITLE,
        content: {
          text: body,
          subtype: 'CHAT',
          aiGenerated: false,
          fromChat: {
            channelId: message.channelId,
            messageId: message.id,
            channelLabel,
            senderId: message.senderId,
            senderName: message.senderName,
            sentAt: message.timestamp,
          },
        },
      });
      setDone(patient.socialName || patient.name);
      onClose();
    } catch (err) {
      console.error('[chat→prontuário] registrar:', err);
      const code = (err as { code?: string })?.code;
      setError(
        code === 'permission-denied'
          ? 'Sem permissão para gravar no prontuário (só médico/admin). Nada foi salvo.'
          : `Não consegui registrar${code ? ` (${code})` : ''}. Nada foi salvo — tente de novo.`,
      );
    } finally {
      setSaving(false);
    }
  };

  const hint = message
    ? `Do chat${channelLabel ? ` ${channelLabel}` : ''} · ${message.senderName} · ${formatChatRecordWhen(
        message.timestamp,
      )}. Revise antes de registrar — entra como evolução e só pode ser editada nas primeiras 24h.`
    : '';

  return (
    <>
      <Modal isOpen={!!message && ready} onClose={onClose} title="Registrar no prontuário" maxWidth="md">
        <div className="space-y-4">
          <p className="rounded-md bg-surface-container px-2.5 py-1.5 text-xs text-on-surface-variant">{hint}</p>

          <div>
            <PatientTypeahead clinicId={clinicId} value={patient} onChange={setPatient} label="Paciente" />
            {!patient && (
              <p className="mt-1 text-xs text-on-surface-variant">Escolha o paciente da ficha onde o recado entra.</p>
            )}
          </div>

          <div>
            <label className="mb-1 block font-mono text-[10px] uppercase tracking-[0.15em] text-on-surface-variant">
              Texto do registro
            </label>
            <textarea
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) submit();
              }}
              rows={7}
              className="w-full resize-y rounded-md border border-outline-variant/40 bg-surface-container-lowest p-2.5 text-sm text-on-surface focus:border-primary focus:outline-none"
            />
          </div>

          {error && (
            <p role="alert" className="rounded-md bg-error/10 px-2.5 py-1.5 text-xs text-error">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button onClick={submit} disabled={!patient || !text.trim()} isLoading={saving}>
              Registrar no prontuário
            </Button>
          </div>
        </div>
      </Modal>
      {done &&
        createPortal(
          <div
            role="status"
            className="fixed bottom-6 left-1/2 z-[1000] flex -translate-x-1/2 items-center gap-2 rounded-full bg-on-surface px-4 py-2 text-sm text-surface shadow-float animate-fade-in"
          >
            <Check size={14} /> Registrado no prontuário de {done}
          </div>,
          document.body,
        )}
    </>
  );
};
